import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import AuditResults from './components/AuditResults';
import PipelineSteps from './components/PipelineSteps';
import './App.css';

const API = 'https://resumify-production.up.railway.app';

export default function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [jdFile, setJdFile] = useState(null);
  const [jdMode, setJdMode] = useState('file'); // 'file' | 'paste'
  const [jdText, setJdText] = useState('');
  const [email, setEmail] = useState('');
  const [emailStep, setEmailStep] = useState('idle');
  const [code, setCode] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [log, setLog] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const cvRef = useRef();
  const jdRef = useRef();

  const toggleDark = () => {
    setDarkMode(d => !d);
    document.documentElement.setAttribute('data-theme', darkMode ? 'light' : 'dark');
  };

  const handleFile = (e, setter) => {
    const file = e.target.files[0];
    if (file) setter(file);
  };

  const handleDrop = useCallback((e, setter) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setter(file);
  }, []);

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setEmailStep('sending');
    try {
      await axios.post(`${API}/api/send-code`, { email });
      setEmailStep('code_sent');
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Failed to send code. Please try again.');
      setEmailStep('idle');
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setEmailError('Please enter the 6-digit code.');
      return;
    }
    setEmailError('');
    setEmailStep('verifying');
    try {
      const res = await axios.post(`${API}/api/verify-code`, { email, code });
      setEligibility(res.data);
      setEmailStep('verified');
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setEmailStep('code_sent');
    }
  };

  const isJdReady = () => {
    if (jdMode === 'file') return !!jdFile;
    if (jdMode === 'paste') return jdText.trim().length > 50;
    return false;
  };

  const handleSubmit = async () => {
    if (!cvFile) { setError('Please upload your CV.'); return; }
    if (!isJdReady()) {
      setError(jdMode === 'file'
        ? 'Please upload the Job Description.'
        : 'Please paste the job description text (minimum 50 characters).');
      return;
    }
    if (emailStep !== 'verified') { setError('Please verify your email first.'); return; }

    setError('');
    setStatus('processing');
    setActiveStep(1);
    setLog([]);
    setResult(null);

    const formData = new FormData();
    formData.append('cv_file', cvFile);
    formData.append('email', email);

    if (jdMode === 'file') {
      formData.append('jd_file', jdFile);
    } else {
      // Convert pasted text to a blob and send as a txt file
      const blob = new Blob([jdText], { type: 'text/plain' });
      formData.append('jd_file', blob, 'job_description.txt');
    }

    const stepTimers = [
      setTimeout(() => setActiveStep(2), 8000),
      setTimeout(() => setActiveStep(3), 18000),
      setTimeout(() => setActiveStep(4), 28000),
      setTimeout(() => setActiveStep(5), 42000),
    ];

    try {
      const res = await axios.post(`${API}/api/tailor`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
      stepTimers.forEach(clearTimeout);
      setActiveStep(6);
      setResult(res.data);
      setLog(res.data.log || []);
      setStatus(res.data.status === 'failed' ? 'failed' : 'done');
    } catch (err) {
      stepTimers.forEach(clearTimeout);
      setActiveStep(0);
      setStatus('failed');
      setError(err.response?.data?.detail || err.message || 'Something went wrong.');
    }
  };

  const handlePayAndDownload = async () => {
    if (!result?.session_id) return;
    setCheckoutLoading(true);
    try {
      if (result.is_free) {
        window.open(`${API}/api/download/${result.session_id}`, '_blank');
        setCheckoutLoading(false);
        return;
      }
      const res = await axios.post(`${API}/api/create-checkout`, {
        session_id: result.session_id
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      const { payfast_url, payfast_data } = res.data;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payfast_url;
      Object.entries(payfast_data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not process payment.');
      setCheckoutLoading(false);
    }
  };

  const reset = () => {
    setStatus('idle'); setResult(null); setError('');
    setCvFile(null); setJdFile(null);
    setJdMode('file'); setJdText('');
    setEmail(''); setCode('');
    setEmailStep('idle'); setEligibility(null); setEmailError('');
    setActiveStep(0); setLog([]);
  };

  const verdictColor = (v) => {
    if (v === 'PASS') return 'var(--success)';
    if (v === 'PASS_WITH_WARNINGS') return 'var(--warning)';
    return 'var(--error)';
  };

  const verdictIcon = (v) => {
    if (v === 'PASS') return '🟢';
    if (v === 'PASS_WITH_WARNINGS') return '🟡';
    return '🔴';
  };

  return (
    <div className="app">
      <nav className="nav">
        <div className="nav-inner">
          <div className="logo"><span className="logo-r">R</span>esumify</div>
          <div className="nav-right">
            <span className="nav-tag">Powered by Claude</span>
            <button className="theme-btn" onClick={toggleDark}>{darkMode ? '☀️' : '🌙'}</button>
          </div>
        </div>
      </nav>

      {status === 'idle' && (
        <section className="hero fade-up">
          <div className="hero-label">AI-Powered CV Tailoring</div>
          <h1 className="hero-title">Land the interview.<br /><span className="hero-accent">Every time.</span></h1>
          <p className="hero-sub">
            Upload your CV and the job description. Our 5-step AI pipeline tailors your CV
            with zero fabrication — verified by an automated integrity audit.
          </p>
          <div className="hero-badges">
            <span className="badge">🎉 First CV free</span>
            <span className="badge">🔒 Integrity Guaranteed</span>
            <span className="badge">⚡ 2–3 min turnaround</span>
            <span className="badge">📄 Professional DOCX output</span>
          </div>
        </section>
      )}

      {status === 'processing' && (
        <section className="pipeline-section fade-in">
          <h2 className="processing-title">Tailoring your CV...</h2>
          <p className="processing-sub">This takes 2–3 minutes. Please don't close this tab.</p>
          <PipelineSteps activeStep={activeStep} />
        </section>
      )}

      {status === 'idle' && (
        <section className="upload-section fade-up" style={{ animationDelay: '0.15s' }}>

          {/* Email verification */}
          <div className="email-wrap">
            <label className="email-label">Your email address</label>
            <div className="email-row">
              <input
                className="email-input" type="email" placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailStep('idle'); setEligibility(null); setEmailError(''); }}
                disabled={emailStep === 'verified'}
              />
              {emailStep !== 'verified' && (
                <button className="send-code-btn" onClick={handleSendCode}
                  disabled={emailStep === 'sending' || !email.includes('@')}>
                  {emailStep === 'sending' ? <span className="spinner" /> : emailStep === 'code_sent' ? 'Resend' : 'Send Code'}
                </button>
              )}
              {emailStep === 'verified' && <div className="verified-badge">✅ Verified</div>}
            </div>

            {(emailStep === 'code_sent' || emailStep === 'verifying') && (
              <div className="code-wrap fade-in">
                <p className="code-hint">Enter the 6-digit code sent to <strong>{email}</strong></p>
                <div className="email-row">
                  <input
                    className="email-input code-input" type="text" placeholder="123456" maxLength={6}
                    value={code}
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setEmailError(''); }}
                  />
                  <button className="send-code-btn" onClick={handleVerifyCode}
                    disabled={emailStep === 'verifying' || code.length !== 6}>
                    {emailStep === 'verifying' ? <span className="spinner" /> : 'Verify'}
                  </button>
                </div>
              </div>
            )}

            {emailStep === 'verified' && eligibility && (
              <div className={`eligibility-msg ${eligibility.is_free ? 'free' : 'paid'}`}>
                {eligibility.message}
              </div>
            )}
            {emailError && <div className="email-error">{emailError}</div>}
          </div>

          {/* File uploads */}
          <div className="upload-grid">

            {/* CV upload */}
            <div
              className={`drop-zone ${cvFile ? 'has-file' : ''}`}
              onDrop={e => handleDrop(e, setCvFile)}
              onDragOver={e => e.preventDefault()}
              onClick={() => cvRef.current.click()}
            >
              <input ref={cvRef} type="file" accept=".pdf,.docx" hidden onChange={e => handleFile(e, setCvFile)} />
              <div className="drop-icon">{cvFile ? '✅' : '📄'}</div>
              <div className="drop-label">{cvFile ? cvFile.name : 'Upload your CV'}</div>
              <div className="drop-hint">{cvFile ? 'Click to change' : 'PDF or DOCX · Click or drag & drop'}</div>
            </div>

            {/* JD — file or paste */}
            <div className="jd-zone">

              {/* Mode toggle */}
              <div className="jd-mode-toggle">
                <button
                  className={`jd-mode-btn ${jdMode === 'file' ? 'active' : ''}`}
                  onClick={() => { setJdMode('file'); setJdText(''); }}
                >
                  📄 Upload File
                </button>
                <button
                  className={`jd-mode-btn ${jdMode === 'paste' ? 'active' : ''}`}
                  onClick={() => { setJdMode('paste'); setJdFile(null); }}
                >
                  📋 Paste Text
                </button>
              </div>

              {/* File mode */}
              {jdMode === 'file' && (
                <div
                  className={`drop-zone ${jdFile ? 'has-file' : ''}`}
                  onDrop={e => handleDrop(e, setJdFile)}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => jdRef.current.click()}
                >
                  <input ref={jdRef} type="file" accept=".pdf,.docx,.jpg,.jpeg,.png" hidden
                    onChange={e => handleFile(e, setJdFile)} />
                  <div className="drop-icon">{jdFile ? '✅' : '💼'}</div>
                  <div className="drop-label">{jdFile ? jdFile.name : 'Upload Job Description'}</div>
                  <div className="drop-hint">{jdFile ? 'Click to change' : 'PDF, DOCX or Image · Click or drag & drop'}</div>
                </div>
              )}

              {/* Paste mode */}
              {jdMode === 'paste' && (
                <div className="paste-zone">
                  <textarea
                    className="jd-textarea"
                    placeholder="Copy the job description from any website and paste it here...

Works with LinkedIn, Indeed, PNet, Careers24, company websites — anywhere."
                    value={jdText}
                    onChange={e => setJdText(e.target.value)}
                    rows={7}
                  />
                  <div className="paste-footer">
                    {jdText.trim().length > 0 ? (
                      <span className={`paste-count ${jdText.trim().length >= 50 ? 'ready' : 'short'}`}>
                        {jdText.trim().length >= 50 ? '✅ Ready' : `${jdText.trim().length} / 50 characters minimum`}
                      </span>
                    ) : (
                      <span className="paste-hint">Tip: Select all text on the job posting page and paste it here</span>
                    )}
                    {jdText.trim().length > 0 && (
                      <button
                        className="paste-clear"
                        onClick={() => setJdText('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-box">{error}</div>}

          <button
            className="tailor-btn"
            onClick={handleSubmit}
            disabled={!cvFile || !isJdReady() || emailStep !== 'verified'}
          >
            <span className="btn-icon">✨</span>
            {eligibility?.is_free === false ? 'Tailor My CV — R25' : 'Tailor My CV — Free'}
          </button>

          <p className="upload-note">Your first CV tailor is completely free. R25 per CV after that.</p>
        </section>
      )}

      {status === 'done' && result && (
        <section className="results-section fade-up">
          <div className="results-header">
            <div className="results-title-row">
              <h2 className="results-title">CV Ready 🎉</h2>
              <button className="reset-btn" onClick={reset}>Start over</button>
            </div>
            <p className="results-sub">
              Tailored for <strong>{result.role_title}</strong> · Candidate: <strong>{result.candidate_name}</strong>
            </p>
          </div>

          <div className="stats-row">
            <div className="stat-card"><div className="stat-num">{result.exp_count}</div><div className="stat-label">Roles processed</div></div>
            <div className="stat-card"><div className="stat-num" style={{ color: 'var(--success)' }}>{result.matched}</div><div className="stat-label">Requirements matched</div></div>
            <div className="stat-card"><div className="stat-num" style={{ color: 'var(--text-muted)' }}>{result.unmatched}</div><div className="stat-label">Omitted (not in CV)</div></div>
            <div className="stat-card"><div className="stat-num" style={{ color: verdictColor(result.verdict) }}>{result.score}/100</div><div className="stat-label">Integrity score</div></div>
          </div>

          <div className="verdict-banner" style={{ borderColor: verdictColor(result.verdict) }}>
            <span className="verdict-icon">{verdictIcon(result.verdict)}</span>
            <div>
              <div className="verdict-title" style={{ color: verdictColor(result.verdict) }}>Audit {result.verdict.replace('_', ' ')}</div>
              <div className="verdict-summary">{result.audit_summary}</div>
            </div>
          </div>

          <AuditResults result={result} />

          <div className="payment-card">
            <div className="payment-left">
              <div className="payment-title">Download your tailored CV</div>
              <div className="payment-desc">
                {result.is_free ? '🎉 This tailor is free — download now!' : 'Professionally formatted DOCX · R25 to download'}
              </div>
            </div>
            <div className="payment-right">
              {result.is_free ? <div className="payment-price free-price">FREE</div> : <div className="payment-price">R25</div>}
              <button className="pay-btn" onClick={handlePayAndDownload} disabled={checkoutLoading}>
                {checkoutLoading ? <span className="spinner" /> : result.is_free ? <>⬇️ Download Free</> : <>💳 Pay &amp; Download</>}
              </button>
            </div>
          </div>

          <details className="log-details">
            <summary>View pipeline log</summary>
            <div className="log-box">
              {log.map((line, i) => (
                <div key={i} className="log-line slide-in" style={{ animationDelay: `${i * 0.05}s` }}>{line}</div>
              ))}
            </div>
          </details>
        </section>
      )}

      {status === 'failed' && (
        <section className="results-section fade-up">
          <div className="fail-card">
            <div className="fail-icon">🔴</div>
            <h2>Processing Issue</h2>
            <p>{error || result?.audit_summary || 'Something went wrong. Please try again.'}</p>
            <button className="tailor-btn" onClick={reset} style={{ marginTop: 24 }}>Try Again</button>
          </div>
        </section>
      )}

      {status === 'idle' && (
        <section className="how-section fade-up" style={{ animationDelay: '0.3s' }}>
          <h2 className="how-title">How it works</h2>
          <div className="how-grid">
            {[
              { icon: '📧', title: 'Verify email', desc: 'Enter your email and confirm with a 6-digit code. Free on first tailor.' },
              { icon: '📤', title: 'Upload files', desc: 'Drop your CV and paste or upload the job description.' },
              { icon: '🤖', title: 'AI Pipeline', desc: '5 specialised AI steps normalise, extract, analyse, tailor, and verify.' },
              { icon: '⬇️', title: 'Download', desc: 'Free on your first tailor. R25 per CV after that.' },
            ].map((item, i) => (
              <div key={i} className="how-card">
                <div className="how-icon">{item.icon}</div>
                <div className="how-card-title">{item.title}</div>
                <div className="how-card-desc">{item.desc}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="footer">
        <div className="footer-inner">
          <span className="logo" style={{ fontSize: 16 }}><span className="logo-r">R</span>esumify</span>
          <span className="footer-copy">© 2025 · resumify.co.za · First CV free · R25 per CV after</span>
          <a href="/terms" style={{ color: 'var(--text-muted)', fontSize: 12 }}>Terms & Conditions</a>
          <a href="/privacy" style={{ color: 'var(--text-muted)', fontSize: 12 }}>Privacy Policy</a>
        </div>
      </footer>
    </div>
  );
}